/**
 * PostDetailDialog — Vollansicht eines Newsfeed-Beitrags mit
 * Anhängen, Lesebestätigung und Kommentaren.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Pin, CheckCircle2, MessageSquare, Send, Trash2, Clock,
  CheckCheck, Paperclip, FileIcon, Download, Image,
} from 'lucide-react';
import { timeAgo, isImageFile, getFileDownloadUrl } from './helpers';

export const PostDetailDialog = ({
  post, onOpenChange, getCategoryById, settings, currentUserId,
  canCreate, canManage,
  commentText, setCommentText,
  onConfirm, onAddComment, onDeleteComment,
}) => {
  if (!post) return null;
  const cat = getCategoryById(post.category_id);

  return (
    <Dialog open={!!post} onOpenChange={(open) => { if (!open) onOpenChange(null); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {post.is_pinned && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 gap-1">
                <Pin className="h-3 w-3" /> Angepinnt
              </Badge>
            )}
            {cat && (
              <Badge variant="outline" className="text-xs"
                style={{ borderColor: cat.color, color: cat.color }}>
                {cat.name}
              </Badge>
            )}
            {post.require_confirmation && (
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 gap-1">
                <CheckCheck className="h-3 w-3" /> Lesebestätigung erforderlich
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl">{post.title}</DialogTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{post.author_name}</span>
            <span>&middot;</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {timeAgo(post.created_at)}
            </span>
          </div>
        </DialogHeader>

        <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{post.content}</div>

        {post.attachments?.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Paperclip className="h-4 w-4" /> Anhänge ({post.attachments.length})
            </h4>
            <div className="grid gap-2">
              {post.attachments.map((att) => (
                <a
                  key={att.id}
                  href={getFileDownloadUrl(att.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`attachment-${att.id}`}
                >
                  {isImageFile(att.content_type)
                    ? <Image className="h-5 w-5 text-blue-500 shrink-0" />
                    : <FileIcon className="h-5 w-5 text-muted-foreground shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{att.original_filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {Math.round((att.size || 0) / 1024)} KB
                    </p>
                  </div>
                  <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {post.require_confirmation && settings.confirmations_enabled && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className={`h-4 w-4 ${post.user_confirmed ? 'text-green-500' : 'text-muted-foreground'}`} />
                  {post.user_confirmed ? 'Sie haben diesen Beitrag bestätigt' : 'Bitte bestätigen Sie die Kenntnisnahme'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {post.confirmation_count} Bestätigung(en) erhalten
                </p>
              </div>
              {!post.user_confirmed && (
                <Button size="sm" onClick={() => onConfirm(post.id)} data-testid="btn-confirm-read">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Gelesen
                </Button>
              )}
            </div>
            {canCreate && post.confirmations?.length > 0 && (
              <div className="mt-3 pt-3 border-t space-y-1">
                <p className="text-xs font-medium text-muted-foreground mb-2">Bestätigt von:</p>
                {post.confirmations.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-xs">
                    <span>{c.user_name}</span>
                    <span className="text-muted-foreground">{timeAgo(c.confirmed_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {settings.comments_enabled && post.allow_comments === false && (
          <div className="mt-6 p-3 rounded-md border border-border bg-muted/40 text-xs text-muted-foreground flex items-center gap-2"
               data-testid="comments-disabled-notice">
            <MessageSquare className="h-3.5 w-3.5" />
            Kommentare sind für diesen Beitrag deaktiviert.
          </div>
        )}

        {settings.comments_enabled && post.allow_comments !== false && (
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Kommentare ({post.comments?.length || 0})
            </h4>
            {post.comments?.length > 0 && (
              <div className="space-y-3">
                {post.comments.map((c) => (
                  <div key={c.id} className="p-3 bg-muted/30 rounded-lg" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{c.author_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                        {(c.author_id === currentUserId || canManage) && (
                          <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={() => onDeleteComment(c.id)}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm mt-1">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Kommentar schreiben..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onAddComment();
                  }
                }}
                data-testid="input-comment"
              />
              <Button size="icon" onClick={onAddComment}
                disabled={!commentText.trim()} data-testid="btn-send-comment">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
